/**
 * 路由兜底页（2026-10-23 新增）。
 *
 * 需求来源（线上事故）：工程经理 Jerry 登录后整页空白 —— 既没有内容也没有报错。
 * 根因不是渲染异常，而是**路由不存在**：他的默认落地页 /rectify（整改看板）
 * 在走查清单「二-7」里随页面一起删掉了，但角色落地映射还指着一个已经没有 <Route> 的路径。
 * React Router 在不匹配任何路由时默认不渲染任何东西，而 Layout 之外的这一层没有兜底，
 * 于是用户看到的就是「整个系统白屏 / error」，且连左侧导航都没有（导航在 Layout 里，同样没匹配上）。
 *
 * 这一层的作用：任何不匹配的路径都不再是空白页，而是
 *   · 明确告知「这个地址不存在」；
 *   · 给「回到我的首页」与「返回上一页」两个出口，用户不会被卡死；
 *   · 附带当前路径，便于用户反馈时直接说清是哪个链接坏了。
 *
 * 关键取舍：挂在 Layout 内部（不是独立整页），这样左侧导航与顶栏仍然可用，
 * 用户能从任何失效链接上继续正常使用系统 —— 这与既有「局部兜底优于整站白屏」的口径一致。
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { defaultLanding } from "../lib/navAccess";
import { Button } from "../components/ui/button";
import { Compass, ArrowLeft, Home } from "lucide-react";

export default function NotFound() {
  const { lang, user } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const loc = useLocation();
  const home = defaultLanding(user) || "/";

  return (
    <div
      className="mx-auto flex max-w-lg flex-col items-center justify-center gap-3 py-20 text-center"
      data-testid="route-not-found"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
        <Compass className="h-6 w-6 text-slate-400" />
      </div>
      <p className="text-lg font-semibold text-slate-700">
        {zh ? "这个页面不存在" : "This page does not exist"}
      </p>
      <p className="text-sm text-slate-500">
        {zh
          ? "链接可能已经变更或被移除。页面其它功能仍可正常使用。"
          : "The link may have changed or been removed. The rest of the system still works."}
      </p>
      <p className="max-w-full break-all rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-400">
        {loc.pathname}
        {loc.search || ""}
      </p>
      <div className="mt-1 flex items-center gap-2">
        <Button size="sm" onClick={() => nav(home, { replace: true })} data-testid="route-not-found-home">
          <Home className="mr-1 h-3.5 w-3.5" />
          {zh ? "回到我的首页" : "Go to my home"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => nav(-1)} data-testid="route-not-found-back">
          <ArrowLeft className="mr-1 h-3.5 w-3.5" />
          {zh ? "返回上一页" : "Back"}
        </Button>
      </div>
    </div>
  );
}
